import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Typography from '@mui/material/Typography';
import type { FC, ReactNode } from 'react';
import { MdBookmark, MdBookmarkBorder, MdPerson, MdPersonOutline } from 'react-icons/md';

import { WidgetCard } from './community-widgets/widget-card';
import { FLUID_TYPE } from './fluid-type';

type QuickFilterRowProps = {
  label: string;
  /** Outline glyph at rest, filled when the filter is on. */
  icon: ReactNode;
  activeIcon: ReactNode;
  active: boolean;
  onToggle: () => void;
};

const QuickFilterRow: FC<QuickFilterRowProps> = ({ label, icon, activeIcon, active, onToggle }) => (
  <ButtonBase
    onClick={onToggle}
    aria-pressed={active}
    sx={{
      width: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-start',
      gap: 1,
      px: 1,
      py: '8px',
      borderRadius: '8px',
      textAlign: 'left',
      bgcolor: active ? theme => `${theme.palette.primary.main}14` : 'transparent',
      transition: 'background-color 0.15s ease',
      '&:hover': {
        bgcolor: active ? theme => `${theme.palette.primary.main}1F` : '#f5f5f5',
      },
    }}
  >
    <Box sx={{ display: 'flex', flexShrink: 0, color: active ? 'primary.main' : '#808080' }}>
      {active ? activeIcon : icon}
    </Box>
    <Typography
      sx={{
        fontFamily: 'Figtree, sans-serif',
        fontSize: FLUID_TYPE.caption,
        fontWeight: active ? 700 : 500,
        color: active ? 'primary.main' : '#0F0F0F',
      }}
    >
      {label}
    </Typography>
  </ButtonBase>
);

interface CommunityQuickFiltersProps {
  saved?: boolean;
  mine?: boolean;
  onToggleSaved: () => void;
  onToggleMine: () => void;
}

/**
 * "My Feed" widget — a right-sidebar card (under Who's Online) that narrows the
 * community feed to the member's own content: "Saved posts" (bookmarks) and
 * "My posts" (authored). Icon toggle rows (filled glyph + primary tint when on).
 * Controlled — the feed owns the state in its URL search params so these combine
 * with the feed filter bar using AND logic.
 */
export const CommunityQuickFilters: FC<CommunityQuickFiltersProps> = ({ saved, mine, onToggleSaved, onToggleMine }) => (
  <WidgetCard title="My Feed">
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
      <QuickFilterRow
        label="Saved posts"
        icon={<MdBookmarkBorder size={16} />}
        activeIcon={<MdBookmark size={16} />}
        active={!!saved}
        onToggle={onToggleSaved}
      />
      <QuickFilterRow
        label="My posts"
        icon={<MdPersonOutline size={16} />}
        activeIcon={<MdPerson size={16} />}
        active={!!mine}
        onToggle={onToggleMine}
      />
    </Box>
  </WidgetCard>
);
